import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, ImagePlus } from "lucide-react";
import UserAccounts from "@/pages/UserAccounts";
import DemoDataCard from "@/components/DemoDataCard";
import { useAuth } from "@/context/AuthContext";


export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [lab, setLab] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const load = () => {
    api.get("/lab").then((r) => {
      setLab(r.data);
      if (r.data.has_logo) {
        api.get("/lab/logo", { responseType: "blob" })
          .then((res) => setLogoUrl(URL.createObjectURL(res.data))).catch(() => {});
      }
    }).catch(() => {});
  };
  useEffect(load, []);




  const saveLab = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/lab", {
        lab_name: lab.lab_name, accession_prefix: lab.accession_prefix,
        address: lab.address, contact_email: lab.contact_email,
        report_footer: lab.report_footer || "",
        rejection_types: (lab.rejection_types || []).map((t) => t.trim()).filter(Boolean),
        tat_target_hours: Number(lab.tat_target_hours) || 48,
        container_presets: (lab.container_presets || [])
          .filter((p) => (p.name || "").trim())
          .map((p) => ({ name: p.name.trim(), parts: Math.max(1, Math.min(Number(p.parts) || 1, 26)) })),
        amendment_delivery_hours: Number(lab.amendment_delivery_hours) || 24,
        retention_block_years: Number(lab.retention_block_years) || 10,
        retention_slide_surgical_years: Number(lab.retention_slide_surgical_years) || 10,
        retention_slide_cytology_years: Number(lab.retention_slide_cytology_years) || 5,
        retention_slide_fna_years: Number(lab.retention_slide_fna_years) || 10,
        retention_report_years: Number(lab.retention_report_years) || 10,
        retention_wet_tissue_days: Number(lab.retention_wet_tissue_days) || 14,
      });
      setLab(data);
      toast.success("Lab profile updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  const fetchLogo = () => {
    api.get("/lab/logo", { responseType: "blob" })
      .then((r) => setLogoUrl(URL.createObjectURL(r.data)))
      .catch(() => setLogoUrl(null));
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/lab/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      const { data } = await api.get("/lab");
      setLab(data);
      fetchLogo();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/lab/logo");
      setLogoUrl(null);
      const { data } = await api.get("/lab");
      setLab(data);
      toast.success("Logo removed");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-7" data-testid="team-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Team & Lab Setup</h1>
        <p className="mt-1 text-sm text-slate-600">Manage the department profile, staff accounts and role-based permissions.</p>
      </div>

      <DemoDataCard />

      {lab && isAdmin && (
        <form onSubmit={saveLab} className="grid gap-4 border border-slate-200 p-4 md:grid-cols-4" data-testid="lab-settings-form">
          <div className="md:col-span-4 text-xs font-semibold uppercase tracking-wide text-slate-600">Department profile</div>
          <Field label="Lab name">
            <Input value={lab.lab_name || ""} onChange={(e) => setLab({ ...lab, lab_name: e.target.value })} data-testid="lab-name-input" className="h-11 border-slate-300" />
          </Field>
          <Field label="Accession prefix">
            <Input value={lab.accession_prefix || ""} onChange={(e) => setLab({ ...lab, accession_prefix: e.target.value })} data-testid="lab-prefix-input" className="h-11 border-slate-300 font-mono" />
          </Field>
          <Field label="TAT target (hours)">
            <Input type="number" value={lab.tat_target_hours || 48} onChange={(e) => setLab({ ...lab, tat_target_hours: e.target.value })} data-testid="lab-tat-input" className="h-11 border-slate-300" />
          </Field>
          <Field label="Contact email">
            <Input value={lab.contact_email || ""} onChange={(e) => setLab({ ...lab, contact_email: e.target.value })} data-testid="lab-email-input" className="h-11 border-slate-300" />
          </Field>
          <Field label="Address (prints on reports)">
            <Input value={lab.address || ""} onChange={(e) => setLab({ ...lab, address: e.target.value })} data-testid="lab-address-input" className="h-11 border-slate-300" />
          </Field>
          <Field label="Report footer">
            <Input value={lab.report_footer || ""} onChange={(e) => setLab({ ...lab, report_footer: e.target.value })}
              placeholder="e.g. CLIA #00D0000000 · Director: Jane Doe, MD" data-testid="lab-footer-input" className="h-11 border-slate-300" />
          </Field>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Report logo</Label>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {lab.has_logo && logoUrl && <img src={logoUrl} alt="Lab logo" className="h-11 w-auto border border-slate-200 object-contain" data-testid="lab-logo-preview" />}
              <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="lab-logo-upload-label">
                <ImagePlus size={15} /> {lab.has_logo ? "Replace logo" : "Upload logo"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="lab-logo-input" />
              </label>
              {lab.has_logo && (
                <button type="button" onClick={removeLogo} data-testid="lab-logo-remove"
                  className="text-xs font-semibold text-red-800 hover:underline">Remove</button>
              )}
              <span className="text-xs text-slate-500">PNG, JPG or WEBP · up to 4 MB</span>
            </div>
          </div>
          <div className="md:col-span-4 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Container label presets
          </div>
          <div className="md:col-span-4 space-y-2" data-testid="container-presets-editor">
            <p className="text-xs text-slate-600">
              Multi-part cases get one container label per part (A, B, C…). Save the counts your providers
              send most often so accessioning picks them in one click.
            </p>
            {(lab.container_presets || []).map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`container-preset-row-${i}`}>
                <Input value={p.name || ""} placeholder="e.g. Prostate needle biopsy"
                       data-testid={`container-preset-name-${i}`} className="h-11 flex-1 border-slate-300"
                       onChange={(e) => {
                         const next = [...lab.container_presets];
                         next[i] = { ...next[i], name: e.target.value };
                         setLab({ ...lab, container_presets: next });
                       }} />
                <Input type="number" min="1" max="26" value={p.parts ?? 1}
                       data-testid={`container-preset-parts-${i}`} className="h-11 w-24 border-slate-300"
                       onChange={(e) => {
                         const next = [...lab.container_presets];
                         next[i] = { ...next[i], parts: e.target.value };
                         setLab({ ...lab, container_presets: next });
                       }} />
                <span className="text-sm text-slate-600">parts</span>
                <button type="button" data-testid={`container-preset-remove-${i}`}
                        onClick={() => setLab({
                          ...lab,
                          container_presets: lab.container_presets.filter((_, x) => x !== i),
                        })}
                        className="text-xs font-semibold text-red-800 hover:underline">Remove</button>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="container-preset-add"
                    onClick={() => setLab({
                      ...lab,
                      container_presets: [...(lab.container_presets || []), { name: "", parts: 1 }],
                    })}
                    className="h-11 border-slate-300">Add preset</Button>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Rejection types</Label>
            <p className="mt-1 text-xs text-slate-600">
              One per line. These are the discrepancies staff can pick when a container arrives unlabeled,
              leaking or wrong.
            </p>
            <Textarea value={(lab.rejection_types || []).join("\n")} data-testid="rejection-types-input"
              onChange={(e) => setLab({ ...lab, rejection_types: e.target.value.split("\n") })}
              className="mt-1 min-h-[150px] border-slate-300 font-mono text-sm" />
          </div>
          <div className="md:col-span-4 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Retention &amp; distribution rules
          </div>
          <Field label="Blocks (years)">
            <Input type="number" min="1" value={lab.retention_block_years ?? 10} data-testid="retention-block-input"
              onChange={(e) => setLab({ ...lab, retention_block_years: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="Surgical slides (years)">
            <Input type="number" min="1" value={lab.retention_slide_surgical_years ?? 10} data-testid="retention-slide-surgical-input"
              onChange={(e) => setLab({ ...lab, retention_slide_surgical_years: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="Cytology slides (years)">
            <Input type="number" min="1" value={lab.retention_slide_cytology_years ?? 5} data-testid="retention-slide-cytology-input"
              onChange={(e) => setLab({ ...lab, retention_slide_cytology_years: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="FNA slides (years)">
            <Input type="number" min="1" value={lab.retention_slide_fna_years ?? 10} data-testid="retention-slide-fna-input"
              onChange={(e) => setLab({ ...lab, retention_slide_fna_years: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="Reports (years)">
            <Input type="number" min="1" value={lab.retention_report_years ?? 10} data-testid="retention-report-input"
              onChange={(e) => setLab({ ...lab, retention_report_years: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="Wet tissue (days after sign-out)">
            <Input type="number" min="1" value={lab.retention_wet_tissue_days ?? 14} data-testid="retention-wet-tissue-input"
              onChange={(e) => setLab({ ...lab, retention_wet_tissue_days: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <Field label="Amended report delivery deadline (hours)">
            <Input type="number" min="1" value={lab.amendment_delivery_hours ?? 24} data-testid="amendment-delivery-input"
              onChange={(e) => setLab({ ...lab, amendment_delivery_hours: e.target.value })} className="h-11 border-slate-300" />
          </Field>
          <div className="md:col-span-4">
            <Button type="submit" data-testid="save-lab-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]"><Save size={15} className="mr-2" /> Save profile</Button>
          </div>        </form>
      )}

      <UserAccounts />

    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
